// components/GoogleReviews.tsx
"use client";

import { useRef, useEffect, useState, useCallback } from 'react';
import { FaStar, FaStarHalfAlt, FaRegStar, FaGoogle } from 'react-icons/fa';

interface Review {
  id: string;
  name: string;
  rating: number;
  text: string;
  date: string;
}

interface GoogleReviewsProps {
  totalReviews?: number;
  averageRating?: number;
}

// ===== Custom hook for auto-scrolling marquee with drag =====
function useMarquee(direction: 'left' | 'right' = 'left', speed: number = 1) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const animationRef = useRef<number | null>(null);

  const scroll = useCallback(() => {
    if (!containerRef.current || isDragging) return;
    const container = containerRef.current;
    const maxScroll = container.scrollWidth - container.clientWidth;
    const step = direction === 'left' ? speed : -speed;
    let newScroll = container.scrollLeft + step;
    if (newScroll >= maxScroll) newScroll = 0;
    if (newScroll < 0) newScroll = maxScroll;
    container.scrollLeft = newScroll;
    animationRef.current = requestAnimationFrame(scroll);
  }, [direction, speed, isDragging]);

  useEffect(() => {
    animationRef.current = requestAnimationFrame(scroll);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [scroll]);

  // Mouse events
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !containerRef.current) return;
    e.preventDefault();
    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    // Resume auto-scroll after a short delay
    setTimeout(() => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      animationRef.current = requestAnimationFrame(scroll);
    }, 1500);
  };

  // Touch events
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!containerRef.current) return;
    setIsDragging(true);
    setStartX(e.touches[0].pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !containerRef.current) return;
    const x = e.touches[0].pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setTimeout(() => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      animationRef.current = requestAnimationFrame(scroll);
    }, 1500);
  };

  return {
    containerRef,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    isDragging,
  };
}

// ===== Main Component =====
export default function GoogleReviews({
  totalReviews = 25000,
  averageRating = 4.9,
}: GoogleReviewsProps) {
  const reviews: Review[] = [
    {
      id: '1',
      name: 'Kamal Yusuf',
      rating: 4.5,
      text: 'Well supported and nice service',
      date: 'Aug 25, 2026',
    },
    {
      id: '2',
      name: 'Chaitanya Agarwal',
      rating: 4.5,
      text: 'Have had a great experience dealing with them. The team is very proactive and always ready to extend help wherever needed.',
      date: 'Aug 25, 2026',
    },
    {
      id: '3',
      name: 'Mohammed Yousaf',
      rating: 4.5,
      text: 'Excellent Service and on time',
      date: 'Aug 23, 2026',
    },
    {
      id: '4',
      name: 'deepali singh',
      rating: 4.5,
      text: 'Hey did very good but the only thing they didn\'t ask me about the company name which I think is important and just made it by my name.',
      date: 'Jul 30, 2026',
    },
    {
      id: '5',
      name: 'Rajul verma',
      rating: 4.5,
      text: 'I took a Delhi address, and my experience was excellent. The entire process was quick and hassle free. Sonam was very helpful, responsive, and ensured everything was completed.',
      date: 'Jul 30, 2026',
    },
    {
      id: '6',
      name: 'Rapaka Sridhar',
      rating: 4.5,
      text: 'Good location for very professional guidance and service from this guide through Sanjana, Diksha.',
      date: 'Jul 30, 2026',
    },
    {
      id: '7',
      name: 'Priya Sharma',
      rating: 5,
      text: 'Best packers and movers in town. They handled my household shifting with utmost care.',
      date: 'Jul 28, 2026',
    },
    {
      id: '8',
      name: 'Vikram Singh',
      rating: 4.5,
      text: 'Professional team, on-time delivery, and no hidden charges. Highly recommended!',
      date: 'Jul 25, 2026',
    },
    {
      id: '9',
      name: 'Ananya Reddy',
      rating: 4.5,
      text: 'The team was punctual and handled my belongings with great care. Would definitely recommend.',
      date: 'Jul 22, 2026',
    },
    {
      id: '10',
      name: 'Suresh Kumar',
      rating: 4.5,
      text: 'Very professional service. They made my move stress-free. Thank you!',
      date: 'Jul 20, 2026',
    },
  ];

  // Duplicate for seamless looping
  const row1Reviews = [...reviews, ...reviews, ...reviews];
  const row2Reviews = [...reviews, ...reviews, ...reviews];

  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    const emptyStars = 5 - Math.ceil(rating);

    return (
      <>
        {[...Array(fullStars)].map((_, i) => (
          <FaStar key={`full-${i}`} className="text-yellow-400 text-xs" />
        ))}
        {hasHalfStar && <FaStarHalfAlt className="text-yellow-400 text-xs" />}
        {[...Array(emptyStars)].map((_, i) => (
          <FaRegStar key={`empty-${i}`} className="text-yellow-400 text-xs" />
        ))}
      </>
    );
  };

  // Marquee hooks
  const row1 = useMarquee('left', 0.8);
  const row2 = useMarquee('right', 0.8);

  return (
    <section className="py-12 md:py-20 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#292929]">
            Trusted by {totalReviews.toLocaleString()}+
          </h2>
          <h3 className="text-base md:text-lg text-[#343434] mt-1">
            Happy customers across India
          </h3>
          <div className="flex items-center justify-center gap-2 mt-3">
            <div className="flex items-center gap-1 text-yellow-400">
              {[...Array(5)].map((_, i) => (
                <FaStar key={i} className="text-sm md:text-base" />
              ))}
            </div>
            <span className="text-[#343434] text-sm font-medium">
              {averageRating} out of 5
            </span>
            <span className="text-[#343434]/60 text-sm">·</span>
            <span className="text-[#343434] text-sm">
              {totalReviews.toLocaleString()}+ reviews
            </span>
            <span className="text-[#343434]/60 text-sm">·</span>
            <a
              href="https://g.page/r/your-place-id/review"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#343434] text-sm font-medium hover:text-[#f62b1e] transition-colors inline-flex items-center gap-1"
            >
              <FaGoogle className="text-sm" /> Write a review
            </a>
          </div>
        </div>

        {/* Marquee Container – with faded edges */}
        <div className="relative">
          {/* Left fade */}
          <div className="absolute left-0 top-0 bottom-0 w-16 md:w-24 z-10 pointer-events-none bg-gradient-to-r from-white to-transparent"></div>
          {/* Right fade */}
          <div className="absolute right-0 top-0 bottom-0 w-16 md:w-24 z-10 pointer-events-none bg-gradient-to-l from-white to-transparent"></div>

          <div className="space-y-4">
            {/* Row 1 – Left to Right */}
            <div
              ref={row1.containerRef}
              className="overflow-x-auto scrollbar-hide cursor-grab active:cursor-grabbing select-none"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              onMouseDown={row1.handleMouseDown}
              onMouseMove={row1.handleMouseMove}
              onMouseUp={row1.handleMouseUp}
              onMouseLeave={row1.handleMouseUp}
              onTouchStart={row1.handleTouchStart}
              onTouchMove={row1.handleTouchMove}
              onTouchEnd={row1.handleTouchEnd}
            >
              <div className="flex gap-4 w-max">
                {row1Reviews.map((review, index) => (
                  <ReviewCard key={`r1-${index}`} review={review} renderStars={renderStars} />
                ))}
              </div>
            </div>

            {/* Row 2 – Right to Left */}
            <div
              ref={row2.containerRef}
              className="overflow-x-auto scrollbar-hide cursor-grab active:cursor-grabbing select-none"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              onMouseDown={row2.handleMouseDown}
              onMouseMove={row2.handleMouseMove}
              onMouseUp={row2.handleMouseUp}
              onMouseLeave={row2.handleMouseUp}
              onTouchStart={row2.handleTouchStart}
              onTouchMove={row2.handleTouchMove}
              onTouchEnd={row2.handleTouchEnd}
            >
              <div className="flex gap-4 w-max">
                {row2Reviews.map((review, index) => (
                  <ReviewCard key={`r2-${index}`} review={review} renderStars={renderStars} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
      `}</style>
    </section>
  );
}

// ===== Review Card =====
function ReviewCard({
  review,
  renderStars,
}: {
  review: Review;
  renderStars: (rating: number) => React.ReactNode;
}) {
  return (
    <div className="flex-shrink-0 w-72 md:w-80 bg-white rounded-md shadow-sm border border-gray-100 p-4 md:p-5 hover:shadow-md transition-shadow select-none">
      <div className="flex items-start justify-between mb-1.5">
        <p className="font-semibold text-[#343434] text-sm md:text-base">{review.name}</p>
        <span className="text-[10px] md:text-xs text-[#343434]/60 flex-shrink-0 ml-2">
          {review.date}
        </span>
      </div>

      <div className="flex items-center gap-0.5 mb-1.5">
        {renderStars(review.rating)}
      </div>

      <p className="text-xs md:text-sm text-[#343434] line-clamp-3">
        {review.text}
      </p>
    </div>
  );
}